'use client';

import type { FC, ReactNode } from 'react';
import { useLocalTranslation } from '../../../../hooks/useLocalTranslation';
import translations from '../RushMid.translations';
import { RpsDiagram } from './RpsDiagram';
import { StanceText } from './StanceText';
import {
  WEAPON_ICON_SRC, GRENADE_ICON_SRC, SMOKE_ICON_SRC, FLASH_ICON_SRC, KNIFE_ICON_SRC, MEDKIT_ICON_SRC,
} from '../../spriteLayout';
import { FaCoins } from '../../../icons';
import { AtlasIcon } from '../shared/AtlasIcon';
import { useGearInfo } from '../useGearInfo';

const SectionTitle: FC<{ children: ReactNode }> = ({ children }) => (
  <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-label-secondary">{children}</h3>
);

const IconRow: FC<{ icon: ReactNode; children: ReactNode }> = ({ icon, children }) => (
  <div className="flex items-center gap-3 rounded-xl bg-white/5 px-3 py-2">
    <div className="flex h-8 w-8 shrink-0 items-center justify-center">{icon}</div>
    <p className="text-sm leading-snug text-white/90">{children}</p>
  </div>
);

const Img: FC<{ src: string; size?: number }> = ({ src, size = 26 }) => (
  <AtlasIcon src={src} size={size} className="select-none" />
);

export const HowToPlayContent: FC = () => {
  const { t } = useLocalTranslation(translations);
  const gear = useGearInfo();

  const steps = [t('howtoLoopStep1'), t('howtoLoopStep2'), t('howtoLoopStep3'), t('howtoLoopStep4')];

  return (
    <div className="flex flex-col gap-6 px-4 pb-6">
      <section>
        <SectionTitle>{t('howtoLoopTitle')}</SectionTitle>
        <ol className="flex flex-col gap-2">
          {steps.map((s, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-blue/20 text-xs font-bold text-accent-blue">
                {i + 1}
              </span>
              <p className="text-sm leading-snug text-white/90">{s}</p>
            </li>
          ))}
        </ol>
      </section>

      <section>
        <SectionTitle>{t('howtoStancesTitle')}</SectionTitle>
        <RpsDiagram />
        <p className="mt-3 text-sm leading-snug text-white/90"><StanceText text={t('howtoStancesText')} /></p>
        <div className="mt-2">
          <IconRow icon={<Img src={KNIFE_ICON_SRC} />}><StanceText text={gear.knife} /></IconRow>
        </div>
      </section>

      <section>
        <SectionTitle>{t('howtoWeaponsTitle')}</SectionTitle>
        <div className="flex flex-col gap-2">
          <IconRow icon={<Img src={WEAPON_ICON_SRC.awp} size={30} />}>{t('howtoWeaponAwp')}</IconRow>
          <IconRow icon={<Img src={WEAPON_ICON_SRC.ak} size={30} />}>{t('howtoWeaponRifle')}</IconRow>
          <IconRow icon={<Img src={WEAPON_ICON_SRC.lmg} size={30} />}>{t('howtoWeaponLmg')}</IconRow>
        </div>
      </section>

      <section>
        <SectionTitle>{t('howtoUtilityTitle')}</SectionTitle>
        <div className="flex flex-col gap-2">
          <IconRow icon={<Img src={SMOKE_ICON_SRC} />}><StanceText text={gear.smoke} /></IconRow>
          <IconRow icon={<Img src={FLASH_ICON_SRC} />}><StanceText text={gear.flash} /></IconRow>
          <IconRow icon={<Img src={GRENADE_ICON_SRC} />}><StanceText text={gear.frag} /></IconRow>
          <IconRow icon={<Img src={MEDKIT_ICON_SRC} />}><StanceText text={gear.medkit} /></IconRow>
        </div>
        <p className="mt-2 text-xs text-label-secondary">{t('howtoSmokeTargetNote')}</p>
        <p className="mt-1 text-xs text-label-secondary">{t('howtoUtilTeamNote')}</p>
      </section>

      <section>
        <SectionTitle>{t('howtoMoneyTitle')}</SectionTitle>
        <div className="flex flex-col gap-2">
          {[t('howtoMoney1'), t('howtoMoney2'), t('howtoMoney3'), t('howtoMoney4')].map((line, i) => (
            <IconRow key={i} icon={<FaCoins size={20} className="text-price" />}>{line}</IconRow>
          ))}
        </div>
      </section>
    </div>
  );
};
